import type { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { type ComputedRef, computed } from "vue";
import type { ProjectWithDetails } from "../../lib/dynamodb/types";
import { getProjectMetadata } from "./useProjectMetadata";

/**
 * Get all project details for a user (metadata only, no event counts)
 */
export async function getProjects(
	projectIds: string[],
	dynamodbProject: { user_table_name: string; client: DynamoDBClient },
): Promise<ProjectWithDetails[]> {
	if (!projectIds || projectIds.length === 0) {
		return [];
	}

	const metadataResults = await Promise.all(
		projectIds.map((id) =>
			getProjectMetadata(id, dynamodbProject).catch(() => {
				return null;
			}),
		),
	);

	const projects: ProjectWithDetails[] = metadataResults
		.map((metadata) => {
			if (!metadata) return null;
			return {
				...metadata,
				role: "member" as const,
				joined_at: metadata.created_at,
			};
		})
		.filter((p): p is ProjectWithDetails => p !== null);

	return projects;
}

export function useProjects(
	userId: ComputedRef<string | null>,
	projectIds: ComputedRef<string[]>,
	dynamodbProject: ComputedRef<{
		user_table_name: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	const enabled = computed(
		() =>
			!!dynamodbProject.value &&
			projectIds.value.length > 0,
	);

	return useQuery({
		queryKey: ["projects", userId, projectIds, credentialsReset],
		queryFn: ({ queryKey: [_, , pIds] }) =>
			getProjects(
				pIds as string[],
				dynamodbProject.value!,
			),
		enabled,
	});
}
